import { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom'
import supabase from '../lib/supabase'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

import AdminHome from '../components/admin/AdminHome'
import ContentManager from '../components/admin/ContentManager'
import ServicesManager from '../components/admin/ServicesManager'
import ProjectsManager from '../components/admin/ProjectsManager'
import InternalProjectsManager from '../components/admin/InternalProjectsManager'
import ProjectFinanceManager from '../components/admin/ProjectFinanceManager'
import ProjectFilesManager from '../components/admin/ProjectFilesManager'
import ProjectUpdatesManager from '../components/admin/ProjectUpdatesManager'
import ClientPortalManager from '../components/admin/ClientPortalManager'
import CategoriesManager from '../components/admin/CategoriesManager'
import BookingsManager from '../components/admin/BookingsManager'
import LeadsManager from '../components/admin/LeadsManager'
import ClientCRMHub from '../components/admin/ClientCRMHub'
import ClientOperationsPage from '../components/admin/ClientOperationsPage'
import PartnersManager from '../components/admin/PartnersManager'
import JobsCalendarManager from '../components/admin/JobsCalendarManager'
import CalendarSyncManager from '../components/admin/CalendarSyncManager'
import KnowledgeManager from '../components/admin/KnowledgeManager'
import ProductsManager from '../components/admin/ProductsManager'
import ModelNormalizationManager from '../components/admin/ModelNormalizationManager'
import UsersManager from '../components/admin/UsersManager'
import ButtonsManager from '../components/admin/ButtonsManager'
import Settings from '../components/admin/Settings'
import ServerInfo from '../components/admin/ServerInfo'

export default function AdminDashboard() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const location = useLocation()

  useEffect(() => { checkUser() }, [])

  const isAdminRole = (role) => role === 'admin' || role === 'superadmin'

  const checkUser = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser?.email) { setLoading(false); return }

      const { data: profile, error } = await supabase
        .from('codevaro_users')
        .select('role, email')
        .eq('email', authUser.email)
        .maybeSingle()

      if (error || !profile || !isAdminRole(profile.role)) {
        if (error) console.error('Error fetching user profile:', error)
        await supabase.auth.signOut()
        setLoading(false)
        return
      }

      setUser(authUser)
    } catch (error) {
      console.error('Error checking user:', error)
      await supabase.auth.signOut()
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try { await supabase.auth.signOut(); setUser(null) }
    catch (error) { console.error('Error logging out:', error) }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
  if (!user) return <Navigate to="/admin/login" replace />

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: FiIcons.FiHome },
    { path: '/admin/content', label: 'Content', icon: FiIcons.FiEdit3 },
    { path: '/admin/projects', label: 'Portfolio Projects', icon: FiIcons.FiGrid },
    { path: '/admin/internal-projects', label: 'Internal Projects', icon: FiIcons.FiBriefcase },
    { path: '/admin/project-finance', label: 'Project Finance', icon: FiIcons.FiDollarSign },
    { path: '/admin/project-files', label: 'Project Files', icon: FiIcons.FiFileText },
    { path: '/admin/project-updates', label: 'Project Updates', icon: FiIcons.FiMessageSquare },
    { path: '/admin/client-portal', label: 'Client Portal', icon: FiIcons.FiShield },
    { path: '/admin/leads', label: 'Leads', icon: FiIcons.FiUsers },
    { path: '/admin/clients', label: 'Clients / CRM', icon: FiIcons.FiBuilding },
    { path: '/admin/client-operations', label: 'Client Operations', icon: FiIcons.FiTool },
    { path: '/admin/partners', label: 'Partners', icon: FiIcons.FiUserCheck },
    { path: '/admin/jobs', label: 'Jobs / Calendar', icon: FiIcons.FiCalendar },
    { path: '/admin/calendar-sync', label: 'Calendar Sync', icon: FiIcons.FiRefreshCw },
    { path: '/admin/products', label: 'Products / Inventory', icon: FiIcons.FiPackage },
    { path: '/admin/model-normalization', label: 'Model Normalization', icon: FiIcons.FiCpu },
    { path: '/admin/knowledge', label: 'Knowledge', icon: FiIcons.FiBookOpen },
    { path: '/admin/services', label: 'Services', icon: FiIcons.FiSettings },
    { path: '/admin/categories', label: 'Categories', icon: FiIcons.FiFolder },
    { path: '/admin/bookings', label: 'Bookings', icon: FiIcons.FiCalendar },
    { path: '/admin/users', label: 'Users', icon: FiIcons.FiUser },
    { path: '/admin/buttons', label: 'Buttons', icon: FiIcons.FiSquare },
    { path: '/admin/settings', label: 'Settings', icon: FiIcons.FiSettings },
    { path: '/admin/server', label: 'Server Info', icon: FiIcons.FiServer }
  ]

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <div className={`bg-white shadow-lg transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'} flex-shrink-0 relative`}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className={`font-bold text-xl text-gray-800 transition-all duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>Admin Panel</h1>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center" style={{ minWidth: '40px', minHeight: '40px' }}>
              <SafeIcon icon={sidebarOpen ? FiIcons.FiChevronLeft : FiIcons.FiChevronRight} className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path} className={`flex items-center px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors ${location.pathname === item.path ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : ''}`}>
              <SafeIcon icon={item.icon} className="h-5 w-5 flex-shrink-0" />
              <span className={`ml-3 transition-all duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <button onClick={handleLogout} className={`w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}>
            <SafeIcon icon={FiIcons.FiLogOut} className="h-5 w-5 flex-shrink-0" />
            <span className={`ml-3 transition-all duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>Logout</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-6">
          <Routes>
            <Route path="/" element={<AdminHome />} />
            <Route path="/content" element={<ContentManager />} />
            <Route path="/projects" element={<ProjectsManager />} />
            <Route path="/internal-projects" element={<InternalProjectsManager />} />
            <Route path="/project-finance" element={<ProjectFinanceManager />} />
            <Route path="/project-files" element={<ProjectFilesManager />} />
            <Route path="/project-updates" element={<ProjectUpdatesManager />} />
            <Route path="/client-portal" element={<ClientPortalManager />} />
            <Route path="/leads" element={<LeadsManager />} />
            <Route path="/clients" element={<ClientCRMHub />} />
            <Route path="/client-operations" element={<ClientOperationsPage />} />
            <Route path="/partners" element={<PartnersManager />} />
            <Route path="/jobs" element={<JobsCalendarManager />} />
            <Route path="/calendar-sync" element={<CalendarSyncManager />} />
            <Route path="/products" element={<ProductsManager />} />
            <Route path="/model-normalization" element={<ModelNormalizationManager />} />
            <Route path="/knowledge" element={<KnowledgeManager />} />
            <Route path="/services" element={<ServicesManager />} />
            <Route path="/categories" element={<CategoriesManager />} />
            <Route path="/bookings" element={<BookingsManager />} />
            <Route path="/users" element={<UsersManager />} />
            <Route path="/buttons" element={<ButtonsManager />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/server" element={<ServerInfo />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}